import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { Home, Search, ShoppingBag, Heart, User, MapPin, Utensils, ChevronDown } from "lucide-react";

export default function CustomerLayout() {
  const { user } = useAuth();
  const { totals } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const isTrackingPage = /^\/track\//.test(location.pathname);

  return (
    <div className="min-h-screen bg-eatup-gray">
      {/* Top Nav (Uber Eats style: address front and center) */}
      {!isTrackingPage && (
        <header className="sticky top-0 z-40 glass">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-3">
            <Link to="/home" className="flex items-center gap-2 shrink-0" data-testid="logo-home">
              <img
                src="/eats-gnd-logo.png"
                alt="Eats GND"
                className="w-10 h-10 rounded-xl object-cover shadow-sm"
                draggable={false}
              />
              <span className="hidden sm:block font-display text-xl font-bold text-eatup-charcoal">
                Eats <span className="text-eatup-green">GND</span>
              </span>
            </Link>

            {/* Big address selector (Uber Eats style) */}
            <button
              onClick={() => navigate("/profile")}
              className="flex-1 sm:flex-none flex items-center gap-2 px-3 py-2 rounded-full hover:bg-neutral-100 transition min-w-0"
              data-testid="address-selector"
            >
              <MapPin className="w-4 h-4 text-eatup-charcoal shrink-0" />
              <div className="text-left leading-tight min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Deliver to</div>
                <div className="text-sm font-bold text-eatup-charcoal truncate max-w-[180px] sm:max-w-none">
                  {user?.address ? user.address.split(",")[0] : "Grand Anse, GD"}
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />
            </button>

            <div className="flex-1 hidden md:block" />

            <nav className="hidden md:flex items-center gap-1">
              {[
                { to: "/home", label: "Home" },
                { to: "/orders", label: "Orders" },
                { to: "/favorites", label: "Favorites" },
              ].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-full text-sm font-semibold transition ${
                      isActive ? "bg-eatup-charcoal text-white" : "text-neutral-600 hover:bg-neutral-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <button
              data-testid="cart-btn"
              onClick={() => navigate("/cart")}
              className="relative p-2.5 rounded-full bg-eatup-charcoal text-white hover:bg-black transition"
            >
              <ShoppingBag className="w-5 h-5" />
              {totals.count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-eatup-green text-white text-[10px] font-bold flex items-center justify-center"
                >
                  {totals.count}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate(user ? "/profile" : "/login")}
              data-testid="profile-btn"
              className="w-10 h-10 rounded-full bg-white border border-neutral-200 text-eatup-charcoal flex items-center justify-center font-semibold overflow-hidden shrink-0"
              title={user ? "Profile" : "Sign in"}
            >
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : user ? (
                (user.name || "U")[0].toUpperCase()
              ) : (
                <span className="text-xs font-black">In</span>
              )}
            </button>
          </div>
        </header>
      )}

      <main className={isTrackingPage ? "" : "pb-24 md:pb-0"}>
        <Outlet />
      </main>

      {/* Bottom nav (mobile — Uber Eats style: solid pill) */}
      {!isTrackingPage && (
        <nav className="md:hidden fixed bottom-4 left-4 right-4 z-40">
          <div className="glass rounded-3xl shadow-2xl px-3 py-2 flex items-center justify-around">
            {[
              { to: "/home", icon: Home, label: "Home" },
              { to: "/orders", icon: ShoppingBag, label: "Orders" },
              { to: "/favorites", icon: Heart, label: "Fav" },
              { to: "/profile", icon: User, label: "Me" },
            ].map((l) => {
              const Icon = l.icon;
              const active = location.pathname === l.to;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`bottom-nav-${l.label.toLowerCase()}`}
                  className={`flex flex-col items-center gap-0.5 px-4 py-2 rounded-2xl transition ${
                    active ? "text-eatup-charcoal" : "text-neutral-400"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? "fill-eatup-green/10" : ""}`} strokeWidth={active ? 2.5 : 2} />
                  <span className="text-[10px] font-bold">{l.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
